import { useHandler } from "@redotech/react-util/hook";
import PrinterIcon from "@redotech/redo-icons/printer.svg";
import type { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { teamHasAvailableReturnsCenter } from "@redotech/redo-return-settings-primitives-common/returns-center-config";
import { toast } from "@redotech/redo-web/alert";
import {
  RedoButton,
  type RedoButtonSize,
} from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { memo, useContext, useState } from "react";
import { DocumentPrintModal } from "../document-print-modal";
import { PrintBatchContext } from "../print-batch-modal";

export const ParcelBoxLabelButton = memo(function ParcelBoxLabelButton({
  fulfillmentGroupId,
  parcelIndex,
  size = "sm",
}: {
  fulfillmentGroupId: string;
  parcelIndex: number;
  size?: RedoButtonSize;
}) {
  const [pending, setPending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { tryPrintParcelBoxLabel } = useContext(PrintBatchContext);
  const team = useContext(TeamContext);
  const hasReturnsCenter = teamHasAvailableReturnsCenter(team);

  const handleClick = useHandler(async (e: React.MouseEvent) => {
    e.stopPropagation();
    setPending(true);
    try {
      const printed = await tryPrintParcelBoxLabel({
        fulfillmentGroupId,
        parcelIndex,
      });
      if (!printed) {
        setPreviewOpen(true);
      }
    } catch {
      toast("Failed to print box label", { variant: "error" });
    } finally {
      setPending(false);
    }
  });

  const fetchPdfUrl = useHandler(
    (client: RedoOutboundLabelsRpcClient, signal: AbortSignal) =>
      client.getParcelBoxLabel({ fulfillmentGroupId, parcelIndex }, { signal }),
  );

  if (!hasReturnsCenter) {
    return null;
  }

  return (
    <>
      <RedoButton
        hierarchy="tertiary"
        IconLeading={PrinterIcon}
        onClick={handleClick}
        pending={pending}
        size={size}
        text="Label"
      />
      {previewOpen && (
        <DocumentPrintModal
          errorMessage="Failed to generate box label"
          fetchPdfUrl={fetchPdfUrl}
          onClose={() => setPreviewOpen(false)}
          subtitle={`Package ${parcelIndex + 1}`}
          title="Print box label"
        />
      )}
    </>
  );
});
